import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';

export interface OutlookMailDialogUser {
  firstName: string;
  lastName: string;
  mail: string | null | undefined;
  nationalId: string;
  userId: number;
}

interface OutlookMailDialogProps {
  visible: boolean;
  onHide: () => void;
  users: OutlookMailDialogUser[];
  title?: string;
}

const hasMail = (user: OutlookMailDialogUser): boolean =>
  !!user.mail && user.mail.trim().length > 0;

type SendMode = 'to' | 'bcc';

export const OutlookMailDialog: React.FC<OutlookMailDialogProps> = ({
  visible,
  onHide,
  users,
  title = 'ייצוא לאאוטלוק',
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(
    new Set(),
  );

  // The two checkboxes ("שליחה לנמענים" / "שליחה מוסתרת") are mutually
  // exclusive so we model them as a single mode. Defaults to BCC (נסתר).
  const [sendMode, setSendMode] = useState<SendMode>('bcc');

  useEffect(() => {
    if (visible) {
      setSelectedUserIds(new Set(users.filter(hasMail).map((u) => u.userId)));
      setSendMode('bcc');
    }
  }, [visible, users]);

  const eligibleUsers = useMemo(() => users.filter(hasMail), [users]);

  const allEligibleSelected =
    eligibleUsers.length > 0 &&
    eligibleUsers.every((u) => selectedUserIds.has(u.userId));

  const toggleUser = (userId: number, checked: boolean) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(new Set(eligibleUsers.map((u) => u.userId)));
    } else {
      setSelectedUserIds(new Set());
    }
  };

  const handleApprove = () => {
    const recipients = users
      .filter((u) => selectedUserIds.has(u.userId) && hasMail(u))
      .map((u) => u.mail!.trim());

    if (recipients.length === 0) {
      toast.warn('יש לבחור לפחות נמען אחד');
      return;
    }
    const separator = navigator.userAgent.includes('Windows') ? ';' : ',';

    const mailtoLink =
      sendMode === 'to'
        ? `mailto:${recipients.join(separator)}`
        : `mailto:?bcc=${recipients.join(separator)}`;
    window.location.href = mailtoLink;
    onHide();
  };

  const selectedCount = useMemo(
    () =>
      users.filter((u) => selectedUserIds.has(u.userId) && hasMail(u)).length,
    [users, selectedUserIds],
  );

  const footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
        נבחרו {selectedCount} מתוך {eligibleUsers.length} נמענים
      </span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          label="ביטול"
          icon="pi pi-times"
          onClick={onHide}
          className="p-button-text"
        />
        <Button
          label="פתיחה באאוטלוק"
          icon="pi pi-envelope"
          onClick={handleApprove}
          className="p-button-success"
          disabled={selectedCount === 0}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      header={title}
      visible={visible}
      onHide={onHide}
      style={{ width: '720px', maxWidth: '95vw' }}
      footer={footer}
      dismissableMask
      draggable={false}
      resizable={false}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          flexWrap: 'wrap',
          padding: '10px 12px',
          marginBottom: '12px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          direction: 'rtl',
        }}
      >
        <span style={{ fontWeight: 600, color: '#374151' }}>שיטת שליחה:</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Checkbox
            inputId="outlook-send-mode-bcc"
            checked={sendMode === 'bcc'}
            onChange={() => setSendMode('bcc')}
          />
          <label
            htmlFor="outlook-send-mode-bcc"
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            שליחה מוסתרת
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Checkbox
            inputId="outlook-send-mode-to"
            checked={sendMode === 'to'}
            onChange={() => setSendMode('to')}
          />
          <label
            htmlFor="outlook-send-mode-to"
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            שליחה לנמענים
          </label>
        </div>
      </div>

      <DataTable
        value={users}
        scrollable
        scrollHeight="60vh"
        dataKey="userId"
        emptyMessage={
          <div style={{ textAlign: 'right' }}>אין משתמשים להצגה</div>
        }
        tableStyle={{ direction: 'rtl' }}
      >
        <Column
          style={{ width: '50px', textAlign: 'center' }}
          header={
            <Checkbox
              checked={allEligibleSelected}
              onChange={(e) => toggleSelectAll(!!e.checked)}
              disabled={eligibleUsers.length === 0}
              tooltip="בחר/בטל הכל"
              tooltipOptions={{ position: 'top' }}
            />
          }
          body={(row: OutlookMailDialogUser) => {
            const enabled = hasMail(row);
            return (
              <Checkbox
                checked={selectedUserIds.has(row.userId)}
                onChange={(e) => toggleUser(row.userId, !!e.checked)}
                disabled={!enabled}
                tooltip={!enabled ? 'אין כתובת מייל למשתמש זה' : undefined}
                tooltipOptions={{ position: 'top' }}
              />
            );
          }}
        />
        <Column
          style={{ textAlign: 'right' }}
          header="שם"
          body={(row: OutlookMailDialogUser) => (
            <span style={{ fontWeight: 500 }}>
              {row.lastName} {row.firstName}
            </span>
          )}
        />
        <Column
          style={{ textAlign: 'right' }}
          header="ת.ז"
          body={(row: OutlookMailDialogUser) => <span>{row.nationalId}</span>}
        />
        <Column
          style={{ textAlign: 'right' }}
          header="מייל"
          body={(row: OutlookMailDialogUser) =>
            hasMail(row) ? (
              <span dir="ltr">{row.mail}</span>
            ) : (
              <span style={{ color: '#9ca3af' }}>לא קיים</span>
            )
          }
        />
      </DataTable>
    </Dialog>
  );
};
